/**
 * RESPONSIBILITY: the House of Honor appeal page.
 *
 * Its own page and its own section, not a band inside Give: the appeal makes a case,
 * where Give answers how to act on it. Sharing a section would mean one pencil opened
 * both, and an editor tidying the appeal's wording would find the giving mechanics
 * sitting in the same dialog.
 *
 * No @EditableBy, so WRITE defaults to NONE — admin only.
 */
import { Section, Text, Paragraph, Img, Link, List, RenderOf } from '@framework/core/decorators';
import type { ImageValue } from '@framework/core';
import { SectionName } from '../section-names';
import { Highlight } from './rows/highlight';
import { ABOVE_HINT, LINK_HINT } from '../hints';

@Section({ name: SectionName.HouseOfHonor, label: 'House of Honor' })
export class HouseOfHonorSection {
  @Text({
    label: 'Subtitle', maxLength: 120, default: 'A Capital Campaign',
    group: 'Page head', description: ABOVE_HINT,
  })
  eyebrow!: string;

  @Text({ label: 'Title', maxLength: 300, default: 'House of Honor', group: 'Page head' })
  heading!: string;

  @Paragraph({
    label: 'Description', maxLength: 600, rows: 3, group: 'Page head',
    description: 'The short line under the title.',
  })
  intro!: string;

  @Img({
    label: 'Image', folder: 'house-of-honor', maxSizeMb: 12, size: '1600 × 1000',
    description: 'The rendering of the building. Shown on this page and in the band on Home.',
  })
  image!: ImageValue;

  @Paragraph({
    label: 'The case', maxLength: 20000, rows: 8, markdown: true, allow: ['verse'],
    group: 'The case', description: 'Why this matters, in the church’s own words.',
  })
  body!: string;

  @RenderOf('body')
  bodyHtml!: string;

  @List({
    label: 'Highlights', of: () => Highlight, maxItems: 4, group: 'The case',
    description: 'Three or four reasons, shown as a row under the case.',
  })
  highlights!: Highlight[];

  @Text({
    label: 'Button text', maxLength: 120, default: 'Give to the appeal', group: 'Buttons',
  })
  ctaLabel!: string;

  @Link({ label: 'Button link', group: 'Buttons', description: LINK_HINT })
  ctaHref!: string;

  @Text({ label: 'Second button text', maxLength: 120, default: 'Ask a question', group: 'Buttons' })
  askLabel!: string;

  @Link({ label: 'Second button link', group: 'Buttons', description: LINK_HINT })
  askHref!: string;
}
